import React, { useState, useEffect } from 'react';
import { UserAccount, Role } from '../types';
import { userService } from '../services/userService';
import api from '../services/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';

const UserManager: React.FC = () => {
  const [users, setUsers] = useState<UserAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
  
  // Provisioning State
  const [zohoSearch, setZohoSearch] = useState('');
  const [zohoResults, setZohoResults] = useState<any[]>([]);
  const [isSearchingZoho, setIsSearchingZoho] = useState(false);
  const [selectedZohoUser, setSelectedZohoUser] = useState<any | null>(null);
  const [assignedRole, setAssignedRole] = useState<Role>('RESERVATIONS');
  const [assignedCharterCap, setAssignedCharterCap] = useState<number>(0);
  const [assignedScheduledCap, setAssignedScheduledCap] = useState<number>(0);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [uploadingSignatureImageFor, setUploadingSignatureImageFor] = useState<string | null>(null);
  const [signatureShareExpandedByUser, setSignatureShareExpandedByUser] = useState<Record<string, boolean>>({});
  const [inboxAccessExpandedByUser, setInboxAccessExpandedByUser] = useState<Record<string, boolean>>({});
  const [inboundInboxOptions, setInboundInboxOptions] = useState<string[]>([]);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  useEffect(() => {
    loadUsers();
    // Get current user from token
    const token = localStorage.getItem('ma_jwt');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserEmail(payload.email || '');
      } catch (err) {
        console.error('Failed to parse JWT');
      }
    }
  }, []);

  // Handle Zoho Search Throttle
  useEffect(() => {
    if (zohoSearch.length < 2) {
      setZohoResults([]);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setIsSearchingZoho(true);
      try {
        const results = await userService.searchZoho(zohoSearch);
        setZohoResults(results);
      } finally {
        setIsSearchingZoho(false);
      }
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [zohoSearch]);

  const showToast = (type: 'success' | 'error' | 'info', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2800);
  };

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const [data, inboxOptions] = await Promise.all([
        userService.getAllUsers(),
        userService.getInboundInboxOptions().catch(() => []),
      ]);
      setUsers(data);
      setInboundInboxOptions(inboxOptions);
    } catch (err) {
      console.error("Failed to load user registry from DB");
      showToast('error', 'Could not load user directory');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectZoho = (user: any) => {
    setSelectedZohoUser(user);
    setZohoSearch('');
    setZohoResults([]);
  };

  const handleCommitProvisioning = async () => {
    if (!selectedZohoUser) return;

    try {
      // Parse name into firstName and lastName
      const nameParts = selectedZohoUser.name.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Map avatar to photoUrl for backend
      const photoUrl = selectedZohoUser.avatar || selectedZohoUser.photoUrl || null;
      
      const created = await userService.createUser({
        email: selectedZohoUser.email,
        firstName,
        lastName,
        role: assignedRole,
        charterDiscountCap: assignedCharterCap,
        scheduledDiscountCap: assignedScheduledCap,
        zohoId: selectedZohoUser.id,
        photoUrl: photoUrl
      });
      
      // Refresh from backend to avoid duplicates and ensure consistency
      await loadUsers();
      setIsAdding(false);
      setSelectedZohoUser(null);
      setAssignedRole('RESERVATIONS');
      setAssignedCharterCap(0);
      setAssignedScheduledCap(0);
      showToast('success', 'User provisioned');
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to provision user');
    }
  };

  // Legacy admin purge kept available via API; UI control removed per request
  const handlePurgeSeeds = async () => {
    try {
      await api.delete('/users/purge-seeds');
      await loadUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    setConfirmDeleteId(userId);
  };

  const handleRoleChange = async (userId: string, newRole: Role) => {
    try {
      await userService.updateUser(userId, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      showToast('success', 'Role updated');
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to update role');
    }
  };

  const handleFieldChange = (userId: string, field: 'personalEmail' | 'phoneNumber' | 'emailSignature', value: string) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, [field]: value } : u));
  };

  const parseStringToArray = (value: unknown): string[] => {
    if (Array.isArray(value)) return value.map((v: any) => String(v || '').trim()).filter(Boolean);
    if (typeof value === 'string' && value.trim()) return value.split(',').map(s => s.trim()).filter(Boolean);
    return [];
  };

  const toggleSignatureShareUser = async (ownerUserId: string, targetUserId: string, checked: boolean) => {
    const owner = users.find(u => u.id === ownerUserId);
    if (!owner) return;
    const existing = parseStringToArray((owner as any).emailSignatureSharedWith);
    const next = checked
      ? Array.from(new Set([...existing, targetUserId]))
      : existing.filter((id: string) => id !== targetUserId);
    setUsers(prev => prev.map(user => {
      if (user.id !== ownerUserId) return user;
      return { ...user, emailSignatureSharedWith: next };
    }));
    setSavingId(ownerUserId);
    try {
      await userService.updateUser(ownerUserId, { emailSignatureSharedWith: next } as any);
      showToast('success', 'Signature access updated');
    } catch (err: any) {
      showToast('error', err?.response?.data?.error || err?.message || 'Failed to save signature access');
      setUsers(prev => prev.map(user => {
        if (user.id !== ownerUserId) return user;
        return { ...user, emailSignatureSharedWith: existing };
      }));
    } finally {
      setSavingId(null);
    }
  };

  const toggleSignatureSharePanel = (userId: string) => {
    setSignatureShareExpandedByUser(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const toggleInboxAccessPanel = (userId: string) => {
    setInboxAccessExpandedByUser(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const setUserInboxAccessMode = (userId: string, mode: 'ALL' | 'LIMITED') => {
    setUsers(prev => prev.map(user => {
      if (user.id !== userId) return user;
      const current = parseStringToArray((user as any).allowedInboundInboxKeys);
      return { ...user, allowedInboundInboxKeys: mode === 'ALL' ? [] : (current.length ? current : inboundInboxOptions.slice(0, 1)) };
    }));
  };

  const toggleUserInboxKey = (userId: string, inboxKey: string, checked: boolean) => {
    const key = String(inboxKey || '').trim().toLowerCase();
    if (!key) return;
    setUsers(prev => prev.map(user => {
      if (user.id !== userId) return user;
      const existing = parseStringToArray((user as any).allowedInboundInboxKeys).map((s: string) => s.toLowerCase());
      const next = checked
        ? Array.from(new Set([...existing, key]))
        : existing.filter((item: string) => item !== key);
      return { ...user, allowedInboundInboxKeys: next };
    }));
  };

  const handleInboxAccessSave = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    const allowedInboundInboxKeys = parseStringToArray((user as any).allowedInboundInboxKeys).map((s: string) => s.toLowerCase());
    setSavingId(userId);
    try {
      const updated = await userService.updateUser(userId, { allowedInboundInboxKeys } as any);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, allowedInboundInboxKeys: (updated as any).allowedInboundInboxKeys || [] } : u));
      showToast('success', 'Inbox access saved');
    } catch (err: any) {
      console.error(err);
      showToast('error', err?.response?.data?.error || err?.message || 'Failed to save inbox access');
    } finally {
      setSavingId(null);
    }
  };

  const handleContactSave = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    setSavingId(userId);
    try {
      const personalEmailRaw = String((user as any).personalEmail || '').trim();
      const phoneRaw = String((user as any).phoneNumber || '').trim();
      const signatureRaw = String((user as any).emailSignature || '').trim();
      const signatureSharedWith = parseStringToArray((user as any).emailSignatureSharedWith);
      const payload: any = {};
      if (personalEmailRaw) payload.personalEmail = personalEmailRaw;
      if (phoneRaw) payload.phoneNumber = phoneRaw;
      if (signatureRaw) payload.emailSignature = signatureRaw;
      payload.emailSignatureSharedWith = signatureSharedWith;
      if (!personalEmailRaw && !phoneRaw && !signatureRaw) {
        payload.personalEmail = null;
      }

      await userService.updateUser(userId, payload);
      showToast('success', 'Contact details saved');
    } catch (err: any) {
      console.error(err);
      console.error('PATCH /users error payload:', err?.response?.data);
      const msg = String(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to save contact details'
      );
      showToast('error', msg);
    } finally {
      setSavingId(null);
    }
  };

  const handleDiscountCapSave = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    setSavingId(userId);
    try {
      await userService.updateUser(userId, {
        charterDiscountCap: Math.max(0, Math.min(100, Number((user as any).charterDiscountCap || 0))),
        scheduledDiscountCap: Math.max(0, Math.min(100, Number((user as any).scheduledDiscountCap || 0))),
      } as any);
      showToast('success', 'Discount caps saved');
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to save discount caps');
    } finally {
      setSavingId(null);
    }
  };

  const uploadSignatureImage = async (userId: string, file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('error', 'Please select an image file');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      showToast('error', 'Image is too large (max 3MB)');
      return;
    }

    setUploadingSignatureImageFor(userId);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const normalized = result.replace(/^data:[^;]+;base64,/i, '');
          resolve(normalized);
        };
        reader.onerror = () => reject(new Error('Failed to read image'));
        reader.readAsDataURL(file);
      });

      const uploaded = await userService.uploadSignatureImage(userId, {
        fileName: file.name,
        mimeType: file.type || 'image/png',
        dataBase64,
      });
      const snippet = String(uploaded?.htmlSnippet || '').trim();
      if (!snippet) {
        showToast('error', 'Upload succeeded but signature snippet was empty');
        return;
      }

      setUsers(prev =>
        prev.map(user => {
          if (user.id !== userId) return user;
          const existing = String((user as any).emailSignature || '').trim();
          const joined = [existing, snippet].filter(Boolean).join('\n');
          return { ...user, emailSignature: joined };
        })
      );
      showToast('success', 'Image uploaded and added to signature');
    } catch (error) {
      console.error(error);
      showToast('error', 'Failed to upload signature image');
    } finally {
      setUploadingSignatureImageFor(null);
    }
  };

  const pasteRichSignatureFromClipboard = async (userId: string) => {
    try {
      const hasClipboardApi = !!(navigator.clipboard && (navigator.clipboard as any).read);
      if (!hasClipboardApi) {
        showToast('error', 'Clipboard rich paste is not supported in this browser');
        return;
      }

      const clipboardItems = await (navigator.clipboard as any).read();
      let htmlPayload = '';
      for (const item of clipboardItems) {
        const types: string[] = Array.isArray(item.types) ? item.types : [];
        if (!types.includes('text/html')) continue;
        const blob = await item.getType('text/html');
        htmlPayload = await blob.text();
        if (htmlPayload.trim()) break;
      }

      if (!htmlPayload.trim()) {
        showToast('error', 'No rich HTML found in clipboard. Copy the signature again from Gmail.');
        return;
      }

      setUsers(prev =>
        prev.map(user =>
          user.id === userId
            ? { ...user, emailSignature: htmlPayload.trim() }
            : user
        )
      );
      showToast('success', 'Rich signature pasted. Review preview and click Save Signature.');
    } catch (error) {
      console.error(error);
      showToast('error', 'Clipboard permission denied or unavailable');
    }
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    const userId = confirmDeleteId;
    setSavingId(userId);
    try {
      await userService.deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      showToast('info', 'User removed');
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to delete user');
    } finally {
      setSavingId(null);
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 pb-24">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">User Directory</h2>
          <p className="text-sm text-slate-500">Update roles, SSO email, and contact channels.</p>
        </div>
        <button 
          onClick={() => setIsAdding(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-all"
        >
          + Provision via Zoho People
        </button>
      </div>

      {isAdding && (
        <div className="bg-white rounded-[4rem] border border-slate-100 p-12 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.1)] animate-in slide-in-from-top-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
          
          <div className="flex justify-between items-center mb-12">
            <div>
              <h3 className="text-xl font-black text-slate-800 uppercase tracking-widest">User Provisioning Node</h3>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2">Step 1: Fetch Corporate Identity</p>
            </div>
            <button onClick={() => setIsAdding(false)} className="text-slate-300 hover:text-rose-500 transition-colors">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Search Side */}
            <div className="space-y-6">
              <div className="space-y-3 relative">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Search Zoho Directory</label>
                <div className="relative">
                  <input 
                    type="text" 
                    placeholder="Enter name or email..." 
                    className="w-full bg-slate-50 border-2 border-slate-50 focus:border-indigo-100 focus:bg-white rounded-[2rem] p-6 text-sm font-bold outline-none transition-all"
                    value={zohoSearch}
                    onChange={e => setZohoSearch(e.target.value)}
                  />
                  {isSearchingZoho && (
                    <div className="absolute right-6 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>

                {/* Search Results Dropdown */}
                {zohoResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-4 bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl z-20 overflow-hidden animate-in fade-in slide-in-from-top-2 max-h-96 overflow-y-auto">
                    {zohoResults.map(res => (
                      <button 
                        key={res.id} 
                        onClick={() => handleSelectZoho(res)}
                        className="w-full flex items-center gap-4 p-5 hover:bg-slate-50 transition-all text-left border-b border-slate-50 last:border-none"
                      >
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 overflow-hidden shrink-0 shadow-sm flex items-center justify-center text-white font-black text-xs">
                          {res.avatar && res.avatar.length > 0 ? (
                            <img src={res.avatar} alt={res.name} className="w-full h-full object-cover" onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }} />
                          ) : null}
                          {(!res.avatar || res.avatar.length === 0) && res.name && (
                            <span>{res.name.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-black text-slate-800">{res.name}</p>
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{res.dept} • {res.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Profile Confirmation Side */}
            <div className={`space-y-8 p-10 rounded-[3rem] transition-all ${selectedZohoUser ? 'bg-slate-50 border border-slate-100' : 'bg-slate-50/50 border border-dashed border-slate-200 opacity-50'}`}>
              {!selectedZohoUser ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  </div>
                  <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Select a profile from Zoho to continue</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2rem] bg-gradient-to-br from-indigo-500 to-purple-600 shadow-xl overflow-hidden border-4 border-white shrink-0 flex items-center justify-center text-white font-black text-2xl">
                      {selectedZohoUser.avatar && selectedZohoUser.avatar.length > 0 ? (
                        <img src={selectedZohoUser.avatar} className="w-full h-full object-cover" alt="User" onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }} />
                      ) : null}
                      {(!selectedZohoUser.avatar || selectedZohoUser.avatar.length === 0) && selectedZohoUser.name && (
                        <span>{selectedZohoUser.name.charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div>
                      <h4 className="text-xl font-black text-slate-900 leading-none mb-2">{selectedZohoUser.name}</h4>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></span>
                        <span className="text-[9px] font-black text-emerald-600 uppercase tracking-[0.2em]">Zoho Verified Identity</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-6 border-t border-slate-200">
                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Assign Operational Role</label>
                      <select 
                        className="w-full bg-white border-none rounded-2xl p-5 font-black text-slate-800 text-sm outline-none shadow-sm appearance-none"
                        value={assignedRole}
                        onChange={e => setAssignedRole(e.target.value as Role)}
                      >
                        <option value="RESERVATIONS">Reservations</option>
                        <option value="FLIGHT_PLANNER">Flight Planner</option>
                        <option value="PILOT">Pilot</option>
                        <option value="FIRST_OFFICER">First Officer</option>
                        <option value="GROUND_OPS">Ground Ops</option>
                        <option value="ACCOUNTS">Accounts</option>
                        <option value="CP">Chief Pilot</option>
                        <option value="DFO">Director Flight Ops</option>
                        <option value="ADMIN">Admin</option>
                        <option value="CEO">CEO</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Charter Discount Cap %</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={assignedCharterCap}
                          onChange={e => setAssignedCharterCap(Math.max(0, Number(e.target.value) || 0))}
                          className="w-full bg-white border-none rounded-2xl p-5 font-black text-slate-800 text-sm outline-none shadow-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Scheduled Discount Cap %</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={assignedScheduledCap}
                          onChange={e => setAssignedScheduledCap(Math.max(0, Number(e.target.value) || 0))}
                          className="w-full bg-white border-none rounded-2xl p-5 font-black text-slate-800 text-sm outline-none shadow-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <button 
                    onClick={handleCommitProvisioning}
                    className="w-full py-6 bg-indigo-600 text-white rounded-[2rem] font-black text-[11px] uppercase tracking-[0.3em] hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all active:scale-95"
                  >
                    Commit to Operational DB
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Cards Grid */}
      {isLoading ? (
        <div className="p-32 flex flex-col items-center justify-center gap-6">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-slate-400">Loading users...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map(user => {
            const displayName = (user as any).name || `${(user as any).firstName || ''} ${(user as any).lastName || ''}`.trim() || (user as any).email || 'Unknown';
            const avatar = (user as any).avatar || (user as any).photoUrl || '';
            const isCurrentUser = (user as any).email === currentUserEmail;
            const position = (user as any).department || (user as any).title || user.role;
            const ssoEmail = (user as any).email || '';
            const personalEmail = (user as any).personalEmail || '';
            const whatsapp = (user as any).phoneNumber || '';
            const emailSignature = (user as any).emailSignature || '';
            const emailSignatureSharedWith: string[] = parseStringToArray((user as any).emailSignatureSharedWith);
            const signatureShareOpen = !!signatureShareExpandedByUser[user.id];
            const charterDiscountCap = Number((user as any).charterDiscountCap || 0);
            const scheduledDiscountCap = Number((user as any).scheduledDiscountCap || 0);
            const allowedInboundInboxKeys: string[] = parseStringToArray((user as any).allowedInboundInboxKeys).map((s: string) => s.toLowerCase());
            const inboxAccessOpen = !!inboxAccessExpandedByUser[user.id];
            const inboxAccessIsAll = allowedInboundInboxKeys.length === 0;
            
            return (
              <div key={user.id} className={`bg-white rounded-2xl border p-6 shadow-sm hover:shadow-md transition-all relative ${
                isCurrentUser ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200'
              }`}>
                {/* Delete Button */}
                <button
                  onClick={() => handleDeleteUser(user.id)}
                  className="absolute top-4 right-4 text-rose-500 hover:text-rose-700 transition-colors"
                  title="Delete user"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>

                {/* User Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl overflow-hidden shrink-0">
                    {avatar ? (
                      <img src={avatar} className="w-full h-full object-cover" alt={displayName} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : (
                      (displayName && displayName.length > 0 ? displayName.charAt(0).toUpperCase() : '?')
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-slate-900 truncate">{displayName}</h3>
                      {isCurrentUser && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">You</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{position}</p>
                  </div>
                </div>

                {/* Role Selector */}
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</label>
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value as Role)}
                      className="mt-1 w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="RESERVATIONS">Reservations</option>
                      <option value="FLIGHT_PLANNER">Flight Planner</option>
                      <option value="PILOT">Pilot</option>
                      <option value="GROUND_OPS">Ground Ops</option>
                      <option value="ACCOUNTS">Accounts</option>
                      <option value="CP">Chief Pilot</option>
                      <option value="DFO">Director Flight Ops</option>
                      <option value="ADMIN">Admin</option>
                      <option value="CEO">CEO</option>
                    </select>
                  </div>

                  {/* SSO Email */}
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      SSO Email
                    </label>
                    <input
                      type="text"
                      value={ssoEmail}
                      readOnly
                      className="mt-1 w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600"
                    />
                  </div>

                  {/* Personal Notification Email */}
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                      Personal Notification Email
                    </label>
                    <input
                      type="text"
                      value={personalEmail}
                      placeholder="personal@gmail.com"
                      onChange={(e) => handleFieldChange(user.id, 'personalEmail', e.target.value)}
                      onBlur={() => handleContactSave(user.id)}
                      className="mt-1 w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={savingId === user.id}
                    />
                  </div>

                  {/* WhatsApp Number */}
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                      WhatsApp Number
                    </label>
                    <input
                      type="text"
                      value={whatsapp}
                      placeholder="+255 7XX XXX XXX"
                      onChange={(e) => handleFieldChange(user.id, 'phoneNumber', e.target.value)}
                      onBlur={() => handleContactSave(user.id)}
                      className="mt-1 w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={savingId === user.id}
                    />
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <button
                      type="button"
                      onClick={() => toggleInboxAccessPanel(user.id)}
                      className="flex w-full items-center justify-between rounded-md px-1 py-1 text-left"
                    >
                      <span>
                        <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Inbound Hub inbox access</span>
                        <span className="mt-1 block text-xs text-slate-600">
                          {inboxAccessIsAll ? 'All inboxes' : allowedInboundInboxKeys.join(', ')}
                        </span>
                      </span>
                      <span className="text-xs font-semibold text-slate-600">{inboxAccessOpen ? 'Hide' : 'Choose'}</span>
                    </button>
                    {inboxAccessOpen ? (
                      <div className="mt-3 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setUserInboxAccessMode(user.id, 'ALL')}
                            className={`rounded-lg border px-3 py-2 text-xs font-semibold ${inboxAccessIsAll ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                          >
                            All inboxes
                          </button>
                          <button
                            type="button"
                            onClick={() => setUserInboxAccessMode(user.id, 'LIMITED')}
                            className={`rounded-lg border px-3 py-2 text-xs font-semibold ${!inboxAccessIsAll ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                          >
                            Selected only
                          </button>
                        </div>
                        {!inboxAccessIsAll ? (
                          <div className="max-h-36 space-y-1 overflow-auto rounded-lg bg-slate-50 p-2">
                            {inboundInboxOptions.length ? inboundInboxOptions.map(inboxKey => (
                              <label key={`${user.id}-${inboxKey}`} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-white">
                                <input
                                  type="checkbox"
                                  checked={allowedInboundInboxKeys.includes(inboxKey)}
                                  onChange={(event) => toggleUserInboxKey(user.id, inboxKey, event.target.checked)}
                                />
                                <span className="text-xs font-semibold text-slate-700">{inboxKey}</span>
                              </label>
                            )) : (
                              <p className="px-2 py-1 text-xs text-slate-500">No inbox keys found yet.</p>
                            )}
                          </div>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => handleInboxAccessSave(user.id)}
                          className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                          disabled={savingId === user.id}
                        >
                          {savingId === user.id ? 'Saving...' : 'Save Inbox Access'}
                        </button>
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email Signature (used in Inbound Hub replies)</label>
                    <textarea
                      value={emailSignature}
                      placeholder={"Best regards,\nYour Name\nRole\nPhone / WhatsApp"}
                      onChange={(e) => handleFieldChange(user.id, 'emailSignature', e.target.value)}
                      onBlur={() => handleContactSave(user.id)}
                      rows={5}
                      className="mt-1 w-full resize-y px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={savingId === user.id}
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void pasteRichSignatureFromClipboard(user.id)}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        disabled={savingId === user.id || uploadingSignatureImageFor === user.id}
                      >
                        Paste Rich Signature
                      </button>
                      <label className={`inline-flex cursor-pointer items-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 ${uploadingSignatureImageFor === user.id ? 'opacity-60 pointer-events-none' : ''}`}>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          className="hidden"
                          onChange={(event) => {
                            const file = event.target.files?.[0] || null;
                            void uploadSignatureImage(user.id, file);
                            event.currentTarget.value = '';
                          }}
                          disabled={uploadingSignatureImageFor === user.id}
                        />
                        {uploadingSignatureImageFor === user.id ? 'Uploading image…' : 'Upload Signature Image'}
                      </label>
                      <button
                        type="button"
                        onClick={() => handleContactSave(user.id)}
                        className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                        disabled={savingId === user.id || uploadingSignatureImageFor === user.id}
                      >
                        {savingId === user.id ? 'Saving…' : 'Save Signature'}
                      </button>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">Supports plain text or HTML. Leave empty to use system default signature.</p>
                    {emailSignature ? (
                      <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3">
                        <button
                          type="button"
                          onClick={() => toggleSignatureSharePanel(user.id)}
                          className="flex w-full items-center justify-between rounded-md px-1 py-1 text-left"
                        >
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Allowed operators to use this signature</span>
                          <span className="text-xs font-semibold text-slate-600">{signatureShareOpen ? 'Hide' : 'Choose'}</span>
                        </button>
                        {signatureShareOpen ? (
                          <div className="mt-2 max-h-36 space-y-1 overflow-auto">
                            {users
                              .filter(candidate => candidate.id !== user.id)
                              .map(candidate => {
                                const label = (candidate as any).name || `${(candidate as any).firstName || ''} ${(candidate as any).lastName || ''}`.trim() || candidate.email || candidate.id;
                                const checked = emailSignatureSharedWith.includes(candidate.id);
                                return (
                                  <label key={`${user.id}-${candidate.id}`} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-slate-50">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(event) => void toggleSignatureShareUser(user.id, candidate.id, event.target.checked)}
                                    />
                                    <span className="text-xs text-slate-700">{label}</span>
                                  </label>
                                );
                              })}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {emailSignature ? (
                      <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Signature Preview</div>
                        <div
                          className="max-h-56 overflow-auto text-sm text-slate-700 [&_img]:max-w-full [&_img]:h-auto"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(emailSignature) }}
                        />
                      </div>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Charter Discount Cap %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={charterDiscountCap}
                        onChange={(e) =>
                          setUsers(prev =>
                            prev.map(u =>
                              u.id === user.id ? { ...u, charterDiscountCap: Math.max(0, Number(e.target.value) || 0) } : u
                            )
                          )
                        }
                        onBlur={() => handleDiscountCapSave(user.id)}
                        className="mt-1 w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={savingId === user.id}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Scheduled Discount Cap %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={scheduledDiscountCap}
                        onChange={(e) =>
                          setUsers(prev =>
                            prev.map(u =>
                              u.id === user.id ? { ...u, scheduledDiscountCap: Math.max(0, Number(e.target.value) || 0) } : u
                            )
                          )
                        }
                        onBlur={() => handleDiscountCapSave(user.id)}
                        className="mt-1 w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={savingId === user.id}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmDeleteId && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-40 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm border border-slate-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-bold">!</div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-slate-900">Delete user?</h4>
                <p className="text-sm text-slate-500 mt-1">This will revoke access for this account.</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmDeleteId(null)} className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg text-sm font-semibold">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-2 text-white bg-rose-600 hover:bg-rose-700 rounded-lg text-sm font-semibold" disabled={savingId === confirmDeleteId}>
                {savingId === confirmDeleteId ? 'Removing...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-semibold text-white ${
          toast.type === 'success' ? 'bg-emerald-500' : toast.type === 'error' ? 'bg-rose-500' : 'bg-slate-800'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default UserManager;
